(() => {
  'use strict';
  if (customElements.get('pt-product-page')) return;

  class ProductPage extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.form = this.querySelector('form');
      this.variants = JSON.parse(this.querySelector('[data-pt-variants]').textContent);
      this.form.addEventListener('change', (event) => {
        if (event.target.name === 'id') this.updateVariant(true);
      }, { signal: this.signal });
      // Keep Shopify's native product POST: every CTA submits the same form.
      this.form.addEventListener('submit', (event) => {
        const variant = this.variant();
        if (!variant || !variant.available || variant.price <= 0) event.preventDefault();
      }, { signal: this.signal });
      this.backNavigation();
      this.boundsCache = new Map();
      this.mediaSelector();
      requestAnimationFrame(() => { this.updateVariant(false); this.sticky(); this.initSectionSpy(); enhance(document); });
    }

    variant() { return this.variants.find((item) => String(item.id) === this.form.elements.id.value); }

    backNavigation() {
      const links = Array.from(this.querySelectorAll('[data-pt-product-back]'));
      if (!links.length) return;

      let previousUrl = '';
      try {
        const referrer = new URL(document.referrer);
        if (referrer.origin === window.location.origin && referrer.href !== window.location.href) {
          previousUrl = referrer.href;
        }
      } catch (_) {}

      links.forEach((link) => {
        if (previousUrl) link.href = previousUrl;
        link.addEventListener('click', (event) => {
          if (!previousUrl || window.history.length <= 1) return;
          event.preventDefault();
          window.history.back();
        }, { signal: this.signal });
      });
    }

    updateVariant(updateUrl) {
      const variant = this.variant();
      if (!variant) return;
      const closing = document.querySelector(`[data-pt-product-closing="${this.dataset.productId}"]`);
      [this, closing].filter(Boolean).forEach((root) => {
        root.querySelectorAll('[data-pt-price]').forEach((el) => { el.textContent = variant.price > 0 ? variant.money : ''; });
        root.querySelectorAll('[data-pt-buy]').forEach((button) => {
          button.disabled = !variant.available || variant.price <= 0;
          button.textContent = button.disabled ? 'Non disponibile' : 'Aggiungi al carrello';
        });
        root.querySelectorAll('[data-pt-variant-title]').forEach((el) => { el.textContent = this.variants.length > 1 ? ` · ${variant.title}` : ''; });
      });
      this.querySelector('[data-pt-availability]').textContent = variant.available && variant.price > 0 ? '' : 'Questo prodotto non è attualmente disponibile per l’acquisto.';
      const quantity = this.form.elements.quantity;
      quantity.min = variant.minimum;
      quantity.step = variant.increment;
      if (variant.maximum == null) quantity.removeAttribute('max');
      else quantity.max = variant.maximum;
      if (updateUrl) {
        quantity.value = variant.minimum;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    }

    mediaSelector() {
      const selector = this.querySelector('[data-pt-media-selector]');
      if (!selector) return;
      const image = selector.querySelector('[data-pt-main-image]');
      const options = [...selector.querySelectorAll('[data-pt-media-option]')];
      if (!image || !options.length) return;

      const initFirst = () => {
        if (!image.naturalWidth) return;
        const bounds = this.measure(image);
        this.currentBounds = bounds;
        this.layoutMedia(image, bounds);
      };
      if (image.complete && image.naturalWidth) {
        initFirst();
      } else {
        image.addEventListener('load', initFirst, { once: true, signal: this.signal });
      }

      if ('ResizeObserver' in window) {
        this.mediaResize = new ResizeObserver(() => {
          if (this.currentBounds) this.layoutMedia(image, this.currentBounds);
        });
        this.mediaResize.observe(image.parentElement);
      }

      // Warm up cache for all option images to eliminate network delay
      options.forEach((option) => {
        if (option.dataset.src) {
          const warm = new Image();
          warm.crossOrigin = 'anonymous';
          warm.src = option.dataset.src;
        }
      });

      let switching = false;
      options.forEach((option) => option.addEventListener('click', async () => {
        if (option.classList.contains('is-selected') || switching) return;
        const nextSrc = option.dataset.src;
        if (!nextSrc) return;
        switching = true;

        options.forEach((item) => {
          const selected = item === option;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });

        const nextAlt = option.dataset.alt || '';
        const loader = new Image();
        loader.crossOrigin = 'anonymous';
        loader.src = nextSrc;
        try {
          if ('decode' in loader) await loader.decode();
          else await new Promise((resolve) => { loader.onload = loader.onerror = resolve; });
        } catch (_) {}

        const nextBounds = this.measure(loader);

        image.style.opacity = '0';
        await new Promise((resolve) => setTimeout(resolve, 140));

        image.src = nextSrc;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.alt = nextAlt;

        this.currentBounds = nextBounds;
        this.layoutMedia(image, nextBounds);

        requestAnimationFrame(() => {
          image.style.opacity = '1';
          switching = false;
        });
      }, { signal: this.signal }));
    }

    measure(img) {
      if (!img || !img.naturalWidth || !img.naturalHeight) return null;
      const key = img.currentSrc || img.src;
      if (this.boundsCache?.has(key)) return this.boundsCache.get(key);

      let result = null;
      try {
        const canvas = document.createElement('canvas');
        const ratio = Math.min(1, 256 / Math.max(img.naturalWidth, img.naturalHeight));
        const w = (canvas.width = Math.round(img.naturalWidth * ratio));
        const h = (canvas.height = Math.round(img.naturalHeight * ratio));
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const pixels = ctx.getImageData(0, 0, w, h).data;

        const cTL = [pixels[0], pixels[1], pixels[2], pixels[3]];
        const cTR = [pixels[(w - 1) * 4], pixels[(w - 1) * 4 + 1], pixels[(w - 1) * 4 + 2], pixels[(w - 1) * 4 + 3]];
        const cBL = [pixels[(h - 1) * w * 4], pixels[(h - 1) * w * 4 + 1], pixels[(h - 1) * w * 4 + 2], pixels[(h - 1) * w * 4 + 3]];
        const cBR = [pixels[((h - 1) * w + w - 1) * 4], pixels[((h - 1) * w + w - 1) * 4 + 1], pixels[((h - 1) * w + w - 1) * 4 + 2], pixels[((h - 1) * w + w - 1) * 4 + 3]];
        const isWhiteCorner = (c) => c[3] > 16 && c[0] >= 240 && c[1] >= 240 && c[2] >= 240;
        const isWhiteBg = isWhiteCorner(cTL) && isWhiteCorner(cTR) && isWhiteCorner(cBL) && isWhiteCorner(cBR);

        let left = w, top = h, right = -1, bottom = -1;
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const idx = (y * w + x) * 4;
            if (pixels[idx + 3] <= 50) continue;
            if (isWhiteBg && pixels[idx] >= 245 && pixels[idx + 1] >= 245 && pixels[idx + 2] >= 245) continue;
            left = Math.min(left, x); right = Math.max(right, x);
            top = Math.min(top, y); bottom = Math.max(bottom, y);
          }
        }

        if (right >= left && bottom >= top && (right - left < w * 0.98 || bottom - top < h * 0.98)) {
          result = {
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight,
            x: ((left + right + 1) / 2 / w) * img.naturalWidth,
            y: ((top + bottom + 1) / 2 / h) * img.naturalHeight,
            width: ((right - left + 1) / w) * img.naturalWidth,
            height: ((bottom - top + 1) / h) * img.naturalHeight,
          };
        }
      } catch (_) {
        result = null;
      }
      this.boundsCache?.set(key, result);
      return result;
    }

    layoutMedia(image, bounds) {
      if (!image || !bounds) {
        if (image) image.classList.remove('is-fitted');
        return;
      }
      const frame = image.parentElement;
      if (!frame) return;
      const { width, height } = frame.getBoundingClientRect();
      if (!width || !height) return;

      const isMobile = window.matchMedia('(max-width: 899px)').matches;
      const availW = isMobile ? Math.max(60, width - 92) : width;
      const scale = 0.88 * Math.min(availW / bounds.width, height / bounds.height);

      image.style.setProperty('--pt-media-width', `${bounds.naturalWidth * scale}px`);
      image.style.setProperty('--pt-media-height', `${bounds.naturalHeight * scale}px`);
      image.style.setProperty('--pt-media-left', `${width / 2 - bounds.x * scale}px`);
      image.style.setProperty('--pt-media-top', `${height / 2 - bounds.y * scale}px`);
      image.classList.add('is-fitted');
    }

    sticky() {
      if (!('IntersectionObserver' in window)) return;
      const bar = this.querySelector('[data-pt-sticky]');
      const purchase = this.querySelector('[data-purchase]');
      const closing = document.querySelector(`[data-pt-product-closing="${this.dataset.productId}"]`);
      let closingVisible = false;
      const render = () => {
        const cookie = document.querySelector('#CybotCookiebotDialog');
        const cookieVisible = cookie && cookie.getBoundingClientRect().height > 0 && getComputedStyle(cookie).visibility !== 'hidden' && getComputedStyle(cookie).display !== 'none';
        bar.hidden = purchase.getBoundingClientRect().bottom >= 0 || closingVisible || Boolean(cookieVisible) || Boolean(document.querySelector('dialog[open]'));
      };
      this.visibility = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.target === closing) closingVisible = entry.isIntersecting; });
        render();
      });
      this.visibility.observe(purchase);
      if (closing) this.visibility.observe(closing);
      window.addEventListener('CookiebotOnAccept', render, { signal: this.signal });
      window.addEventListener('CookiebotOnDecline', render, { signal: this.signal });
      window.addEventListener('CookiebotOnDialogDisplay', render, { signal: this.signal });
      window.addEventListener('CookiebotOnDialogHide', render, { signal: this.signal });
      window.addEventListener('scroll', render, { passive: true, signal: this.signal });
      render();
    }

    initSectionSpy() {
      const navLinks = Array.from(this.querySelectorAll('[data-pt-nav-link]'));
      if (!navLinks.length) return;
      const sectionMap = new Map();
      navLinks.forEach((link) => {
        const hash = link.getAttribute('href');
        if (!hash || !hash.startsWith('#')) return;
        const target = document.querySelector(hash);
        if (target) sectionMap.set(target, link);
      });
      if (!sectionMap.size) return;

      const setActive = (activeLink) => {
        navLinks.forEach((link) => {
          const isActive = link === activeLink;
          link.classList.toggle('is-active', isActive);
          if (isActive) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
      };

      const visibleSections = new Set();
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleSections.add(entry.target);
          } else {
            visibleSections.delete(entry.target);
          }
        });
        if (visibleSections.size > 0) {
          let activeSection = null;
          sectionMap.forEach((_, section) => {
            if (!activeSection && visibleSections.has(section)) {
              activeSection = section;
            }
          });
          if (activeSection) setActive(sectionMap.get(activeSection));
        }
      }, { rootMargin: '-15% 0px -65% 0px', threshold: 0 });

      sectionMap.forEach((_, section) => observer.observe(section));
      this.sectionObserver = observer;

      navLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          const hash = link.getAttribute('href');
          if (!hash || !hash.startsWith('#')) return;
          const target = document.querySelector(hash);
          if (!target) return;
          event.preventDefault();
          const utility = this.querySelector('.pt-product__utility');
          const utilityHeight = utility ? utility.getBoundingClientRect().height : 42;
          const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 84;
          const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - (headerHeight + utilityHeight));
          window.scrollTo({ top, behavior: 'smooth' });
          history.replaceState(null, '', hash);
          setActive(link);
        }, { signal: this.signal });
      });
    }

    disconnectedCallback() {
      this.controller?.abort(); this.visibility?.disconnect(); this.mediaResize?.disconnect(); this.sectionObserver?.disconnect(); this.ready = false;
    }
  }

  function enhance(root) {
    root.querySelectorAll('[data-pt-reading]:not([data-reading-ready])').forEach((reader) => {
      reader.dataset.readingReady = 'true';
      const copy = reader.querySelector('[data-reading-text]');
      const container = copy.querySelector('.metafield-rich_text_field') || copy;
      const blocks = Array.from(container.children);
      const firstParagraph = blocks.findIndex((block) => !/^H[1-6]$/.test(block.tagName));
      const visibleCount = Math.max(1, firstParagraph + 1);
      const button = reader.querySelector('[data-reading-toggle]');
      if (copy.textContent.length < 650 || blocks.length <= visibleCount) return;
      button.hidden = false;
      let expanded = false;
      const update = () => {
        blocks.slice(visibleCount).forEach((block) => { block.toggleAttribute('data-reading-collapsed', !expanded); });
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? 'Riduci il testo' : 'Continua a leggere';
      };
      button.addEventListener('click', () => { expanded = !expanded; update(); });
      update();
    });
    root.querySelectorAll('[data-pt-actives]:not([data-actives-ready])').forEach((group) => {
      group.dataset.activesReady = 'true';
      const items = Array.from(group.querySelectorAll('details'));
      const button = group.querySelector('[data-actives-all]');
      button.hidden = items.length < 2;
      const sync = () => {
        const allOpen = items.every((item) => item.open);
        button.setAttribute('aria-expanded', String(allOpen));
        button.textContent = allOpen ? 'Riduci gli approfondimenti' : 'Leggi tutti gli attivi';
      };
      button.addEventListener('click', () => {
        const open = !items.every((item) => item.open);
        items.forEach((item) => { item.open = open; }); sync();
      });
      items.forEach((item) => item.addEventListener('toggle', sync));
      sync();
    });
    const syncReviews = () => {
      const section = root.querySelector('[data-pt-reviews-section]');
      if (!section) return;
      const yotpoId = section.dataset.yotpoProductId;
      if (!yotpoId) return;
      const navLink = document.querySelector('[data-pt-nav-reviews]');

      fetch(`https://api.yotpo.com/products/iNHpvtEzPlgiRfOzqI21M5sZOGIVAKYS2RdR9ebh/${encodeURIComponent(yotpoId)}/bottomline`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const total = data?.response?.bottomline?.total_reviews || 0;
          if (total > 0) {
            section.hidden = false;
            if (navLink) navLink.hidden = false;
          } else {
            section.hidden = true;
            if (navLink) navLink.hidden = true;
          }
        })
        .catch(() => {});
    };
    syncReviews();
  }
  customElements.define('pt-product-page', ProductPage);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => enhance(document), { once: true });
  else enhance(document);
  document.addEventListener('shopify:section:load', () => enhance(document));
})();
